import styled from "styled-components/macro";
import { focusVisible, focusVisibleInset } from "../../styles/a11y";

export const ModelSelectionContainer = styled.div`
  background: ${(props) => (props.hasHeadline ? "url(/assets/moonbeam-background.png)" : "transparent")};
  background-size: cover;
  background-position: center;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: ${(props) => (props.hasHeadline ? "0" : "20px")};
  padding-right: ${(props) => (props.hasHeadline ? "0" : "30px")};
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -20px;
  margin-left: -20px;
  width: ${(props) => (props.hasHeadline ? "calc(100% + 40px)" : "unset")};
  border-bottom: ${(props) => (props.hasHeadline ? "1px solid var(--divider)" : "none")};
`;

export const Description = styled.div`
  font-size: 16px;
  line-height: 140%; /* 22.4px */
  width: 100%;
  filter: ${(props) => (props.hasHeadline ? "drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.18))" : "none")};

  .headline {
    margin-top: 32px;
    margin-bottom: 18px;
    text-align: center;
    svg {
      path {
        fill: #383838;
      }
    }
  }

  .animated-text-italic {
    font-style: italic;
  }

  .description-text {
    /* Descriptions may carry a paragraph break (blank line). */
    white-space: pre-line;
    margin-top: ${(props) => (props.hasHeadline ? "0px" : "8px")};
    text-align: ${(props) => (props.hasHeadline ? "center" : "left")};
    font-family: ${(props) => (props.hasHeadline ? "GT Super Text" : "Inter")};
    font-size: ${(props) => (props.hasHeadline ? "18px" : "16px")};
    max-width: ${(props) => (props.hasHeadline ? "300px" : "none")};
    margin-left: ${(props) => (props.hasHeadline ? "auto" : "0")};
    margin-right: ${(props) => (props.hasHeadline ? "auto" : "0")};
    color: ${(props) => (props.hasHeadline ? "#383838" : "var(--grey-0)")};
  }
`;

export const ButtonWrapper = styled.div`
  text-align: ${(props) => props.align};
  margin-bottom: ${(props) => (props.modelId === "muse" ? "15px" : "0")};
  margin-top: 20px;
  width: 100%;

  ${(props) =>
    props.modelId === "muse" &&
    `
    position: relative;
    display: inline-block;

    .muse-button-container {
      position: relative;
      display: inline-block;
    }

    .muse-mask {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 10px);
      height: calc(100% + 10px);
      border-radius: 15px;
      border: 1px solid var(--primary-cta-transparent-30);
      overflow: hidden;
      z-index: 0;
    }

    .muse-glow-outer, .muse-glow-inner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 300%;
      height: 300%;
      z-index: 0;
    }

    .muse-glow-outer {
      background: conic-gradient(
        from 180deg at 50% 50%,
        var(--primary-cta-transparent) 45deg,
        var(--primary-cta-transparent-30) 88.12500178813934deg,
        var(--primary-cta-high-contrast) 116.24999642372131deg,
        var(--primary-cta-high-contrast) 129.375deg,
        var(--primary-cta-transparent-30) 165.0000035762787deg,
        var(--primary-cta-transparent) 193.12499284744263deg,
        var(--primary-cta-transparent) 360deg
      ),
      var(--primary-cta-transparent-15);
      filter: blur(12px);
      animation: rotate-background 12s linear infinite;
      opacity: 0.8;
    }

    .muse-glow-inner {
      background: conic-gradient(
        from 180deg at 50% 50%,
        var(--primary-cta-transparent) 45deg,
        var(--primary-cta-transparent-30) 88.12500178813934deg,
        var(--primary-cta) 116.24999642372131deg,
        var(--primary-cta) 129.375deg,
        var(--primary-cta-transparent-30) 165.0000035762787deg,
        var(--primary-cta-transparent) 193.12499284744263deg
      ),
      var(--primary-cta-transparent-15);
      animation: rotate-background 12s linear infinite;
      opacity: 0.6;
    }

    @keyframes rotate-background {
      0% {
        transform: translate(-50%, -50%) rotate(0deg);
      }
      100% {
        transform: translate(-50%, -50%) rotate(360deg);
      }
    }

    button {
      position: relative;
      z-index: 1;
    }
  `}
`;

export const OverlayStyle = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(55, 55, 55, 0.7);
  z-index: ${(props) => props.$zIndex ?? 100000001};
  /* Radix Dialog sets body { pointer-events: none }; restore hit-testing when
     this overlay is layered above Style Settings (elevated overlayZIndex). */
  ${(props) =>
    props.$pointerEvents != null ? `pointer-events: ${props.$pointerEvents};` : ""}

  opacity: 0;
  transition: opacity 300ms ease-in-out;

  &.ReactModal__Overlay--after-open {
    opacity: 1;
  }

  &.ReactModal__Overlay--before-close {
    opacity: 0;
  }
`;

export const ModalStyle = styled.div`
  position: absolute;
  top: 5%;
  left: 50%;
  right: auto;
  bottom: auto;
  margin-right: -50%;
  z-index: 20000;
  width: 810px;
  transform: translate(-50%, 0);
  border-radius: 15px;
  border: 0px;
  padding: 0px;
  box-shadow: var(--default-drop-shadow);
  height: 700px;
  max-height: 90%;

  display: flex;
  justify-content: space-between;
  text-align: left;
  user-select: auto;
  background: var(--menu-color-fullscreen);
  color: var(--grey-0);
  overflow: hidden;

  transition: margin-top 300ms ease-in-out;
  margin-top: 15px;

  &.ReactModal__Content--after-open {
    margin-top: 0;
  }

  &.ReactModal__Content--before-close {
    margin-top: -15px;
  }

  .left-panel {
    display: flex;
    flex-direction: column;
    width: 230px;
    flex-shrink: 0;
    border-right: 1px solid var(--divider);

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 14px;
      border-bottom: 1px solid var(--grey-3);

      h2 {
        font-size: 20px;
        font-weight: 600;
        line-height: 120%;
      }

      .help-button {
        line-height: 24px;
        border-radius: 25px;
        height: 25px;
        width: 25px;
        position: relative;
        color: var(--grey-0);
        display: block;
        padding: 0;
        border: none;
        font: inherit;
        font-weight: bold;
        font-size: 17px;
        text-align: center;
        background: var(--menu-color-active);
        margin-left: 8px;
        cursor: pointer;
        ${focusVisible}
      }
    }

    .model-list-main {
      height: 100%;
    }

    .model-list {
      padding: 8px;
      display: flex;
      flex-direction: column;

      &.model-list-expandable {
        overflow-y: auto;
        scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
        padding: 0px;

        flex-grow: 1;
        height: 0px;
        gap: 4px;

        &::-webkit-scrollbar {
          width: 8px;
        }
        &::-webkit-scrollbar-track {
          background: var(--scrollbar-track);
        }
        &::-webkit-scrollbar-thumb {
          background-color: var(--scrollbar-thumb);
          border-radius: 5px;
        }
        &::-webkit-scrollbar-thumb:hover {
          background-color: var(--scrollbar-thumb-hover);
        }

        opacity: 0;
        max-height: 0;
        transition:
          opacity 0.2s,
          max-height 0.2s;

        &.expanded {
          opacity: 1;
          max-height: 100%;
        }
      }

      .model-list-item-main + .model-list-item-main {
        margin-top: 4px;
      }

      li {
        padding: 8px 11px;
        font-weight: 500;
        border-radius: 8px;
      }

      .model-list-item-simple,
      .model-list-item-main {
        cursor: pointer;
        transition: background-color 0.2s;
        ${focusVisibleInset}

        .new {
          font-size: 9px;
          background: var(--primary-cta);
          border-radius: 3px;
          padding: 2px 3px;
          color: var(--cta-text-color);
        }

        .icon {
          svg {
            path {
              fill: var(--grey-0);
            }
          }
        }

        .expand-button {
          svg {
            path {
              fill: var(--grey-0);
            }
          }
        }

        &:hover {
          background-color: var(--list-element-hover);
        }

        &.active {
          background: var(--selected-element-background);
          color: var(--selected-element-text);

          .icon {
            svg {
              path {
                fill: var(--selected-element-text);
              }
            }
          }

          .expand-button {
            svg {
              path {
                fill: var(--selected-element-text);
              }
            }
          }

          /* Dark themes select rows with --primary-cta, the tag's own background. */
          .new {
            box-shadow: 0 0 0 1px var(--selected-element-text);
          }
        }
      }

      .model-list-item-simple {
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
      }

      .model-list-item-category {
        color: var(--grey-2);
        font-size: 14px;
        line-height: 120%; /* 16.8px */
      }

      .model-list-item-simple + .model-list-item-category {
        margin-top: 10px;
      }

      .model-list-item-main {
        display: flex;
        flex-direction: row;
        gap: 12px;
        align-items: center;
        padding: 8px 7px;

        .expand-button {
          transition: transform 0.2s;
          svg {
            width: 16px;
            height: 16px;
          }
        }

        &.expanded {
          .expand-button {
            transform: scaleY(-1);
          }
        }

        .icon {
          text-align: center;
          flex-shrink: 0;
          width: 24px;

          svg {
            width: 24px;
            height: 24px;
          }
        }

        .labels {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 2;
          flex-grow: 1;

          .label {
            font-size: 16px;
            line-height: 19.2px;

            .new {
              margin-left: 8px;
              vertical-align: 2px;
            }
          }

          .sublabel {
            font-size: 12px;
            line-height: 14.4px;
          }
        }
      }
    }
  }

  .model {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 20px;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
    font-weight: 500;
    gap: 16px;

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: var(--scrollbar-track);
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--scrollbar-thumb);
      border-radius: 5px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: var(--scrollbar-thumb-hover);
    }

    /* Special styling for when has-headline is true */
    &[data-has-headline="true"] {
      padding-top: 0px;
      .icon-button-close {
        svg {
          path {
            fill: #383838; /* Dark color for light backgrounds */
          }
        }
      }
    }

    .icon-button-close {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 10;

      .hover-tip {
        max-width: 50px;
        background: ${(props) => (props.customBackground ? "rgba(0, 0, 0, 0.7)" : "var(--grey-0)")};
        color: ${(props) => (props.customBackground ? "#ffffff" : "var(--context-menu-text)")};
      }

      /* Target SVG based on parent data-attribute */
      svg {
        path {
          /* Default to theme-appropriate color */
          fill: var(--grey-0);
        }
      }

      .icon-button-wrapper {
        &:hover {
          .icon-wrapper {
            background: ${(props) => (props.customBackground ? "rgba(255, 255, 255, 0.15)" : null)} !important;
          }
        }

        &:active {
          .icon-wrapper {
            background: ${(props) => (props.customBackground ? "rgba(255, 255, 255, 0.25)" : null)} !important;
          }
        }
      }
    }

    .attributes {
      margin-top: 8px;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;

      .attribute {
        padding: 8px 8px 6px 8px;
        border: 1px solid var(--grey-3);
        border-radius: 8px;

        .label {
          font-size: 10px;
          line-height: 130%;
          margin-bottom: 4px;
        }
      }

      .attribute-text {
        font-size: 16px;
        line-height: 130%;
      }

      .attribute-money {
        padding: 2px 0px;
        font-size: 14px;
        .inactive {
          color: var(--grey-3);
        }
      }

      .attribute-number {
        padding: 4px 0px;
        display: flex;
        gap: 2px;

        .active,
        .inactive {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          position: relative;

          background-color: var(--grey-3);
          overflow: hidden;

          &:after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            transition: all 0.2s;
          }
        }

        .active:after {
          background-color: var(--primary-cta);
          transform: scale(1);
        }

        .inactive:after {
          transform: scale(0);
        }

        .active:nth-child(1):after {
          transition-delay: 0s;
        }
        .inactive:nth-child(1):after {
          transition-delay: 0.4s;
        }
        .active:nth-child(2):after {
          transition-delay: 0.1s;
        }
        .inactive:nth-child(2):after {
          transition-delay: 0.3s;
        }
        .active:nth-child(3):after {
          transition-delay: 0.2s;
        }
        .inactive:nth-child(3):after {
          transition-delay: 0.2s;
        }
        .active:nth-child(4):after {
          transition-delay: 0.3s;
        }
        .inactive:nth-child(4):after {
          transition-delay: 0.1s;
        }
        .active:nth-child(5):after {
          transition-delay: 0.4s;
        }
        .inactive:nth-child(5):after {
          transition-delay: 0s;
        }
      }

      .tag {
        padding: 6px 11px 6px 8px;
        display: flex;
        gap: 8px;
        align-items: center;
        border: 1px solid var(--grey-3);
        border-radius: 8px;
        align-self: start;

        svg path {
          fill: var(--primary-cta);
        }
      }
    }

    .great-for {
      margin-top: 16px;
      margin-bottom: 8px;
      font-family: Inter;
      font-size: 14px;
      color: var(--grey-1);

      strong {
        color: var(--grey-0);
      }
    }

    .writing-sample {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 0px;
      flex-shrink: 1;

      header {
        padding: 12px 0px 0px 0px;
        color: var(--grey-0);
        font-size: 16px;
        line-height: 100%;
        flex-shrink: 0;
        margin-bottom: 0px;
      }

      .writing-sample-subhead {
        padding: 0px 0px 8px 0px;
        font-size: 14px;
        line-height: 140%;
        color: var(--grey-1);
        margin-top: 5px;
      }

      .samples {
        padding: 0px 0px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        font-size: 14px;
        line-height: 140%;
        flex-shrink: 1;
        overflow-y: auto;
      }
    }

    .creativity-slider {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      gap: 40px;
      margin-top: 15px;
      padding-right: 10px;
      align-items: center;

      .creativity-text {
        h3 {
          font-weight: 500;
          margin-bottom: 5px;
        }
        .creativity-subhead {
          color: var(--grey-1);
          font-size: 14px;
          line-height: 1.4;
          max-width: 220px;
          margin: 0;
        }
      }
    }
  }

  .help {
    position: absolute;
    top: 0;
    left: 231px;
    right: 0;
    bottom: 0;
    background: var(--menu-color);
    z-index: 15;

    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 52px 20px 20px;
    font-weight: 500;

    font-size: 16px;
    line-height: 140%; /* 22.4px */

    transform-origin: top left;
  }

  button {
    font-size: 16px;
    align-self: flex-end;
    background: var(--primary-cta);
    margin-top: auto;
    ${focusVisible}

    &:hover {
      background: var(--primary-cta-hover);
    }

    &:disabled {
      background: var(--grey-2) !important;
      cursor: default;
    }

    color: white;
    border: 0;
    border-radius: 8px;
    font-family: Inter;
    font-weight: 500;
    padding: 10px 16px;
    cursor: pointer;
  }
`;

export const UserStyleExamplesContainer = styled.div`
  .title {
    font-weight: 500;
    color: var(--grey-0);
    margin-top: 15px;
  }

  .description {
    color: var(--grey-1);
    line-height: 1.5;
    margin-top: 5px;
    font-size: 14px;
  }

  textarea.short {
    min-height: 62px !important;
    padding: 10px;
    padding-bottom: 30px;
    background: var(--menu-color-fullscreen);
    font-size: 14px;
    line-height: 1.5;
    font-family: Inter;
    color: var(--grey-0);
    width: calc(100% - 20px);
    transition:
      padding-bottom 0.2s ease-in-out,
      height 0.2s ease-in-out;
  }

  div.textarea-info {
    position: relative;
    margin-top: -12px !important;
    margin-right: 25px !important;
    &.no-help {
      margin-top: -26px !important;
    }
  }


  textarea:focus {
    border: 1px solid var(--primary-cta);
    padding-bottom: 30px;
  }
`;
